import { Modal, ModalBody, ModalContent, ModalOverlay } from '@chakra-ui/modal';
import Card from 'components/card';
import DateRangePicker from 'components/dataPicker';
import SearchSelector from 'components/fields/SearchSelector';
import { set } from 'date-fns';
import { useState } from 'react';
import { downloadPaiementExport, downloadComptableExport, downloadSepaXml } from 'interfaces/invoice';

const PaiementExportModal = (props: {
    isOpen: any;
    onClose: any;
    handleExport?: any;
    archived?: boolean;
}) => {
    const { isOpen, onClose } = props;

    const [filter, setFilter] = useState({
        startDate: new Date(new Date().getTime() - 7 * 24 * 60 * 60 * 1000),
        endDate: new Date(),
        paymentMethod: 'all',
    });

    const defaultCollectionDate = new Date(new Date().getTime() + 5 * 24 * 60 * 60 * 1000)
        .toISOString()
        .slice(0, 10);
    const minCollectionDate = new Date(new Date().getTime() + 24 * 60 * 60 * 1000)
        .toISOString()
        .slice(0, 10);

    const [sepaOptions, setSepaOptions] = useState({
        requestedCollectionDate: defaultCollectionDate,
        sequenceType: 'RCUR',
    });

    const [loading, setLoading] = useState(false);

    const handleDateChange = (dates: any) => {
        const startDate = dates.startDate;
        const endDate = dates.endDate;
        setFilter((prevFilter: any) => ({
            ...prevFilter,
            startDate,
            endDate,
        }));
    };

    const getDateRange = () => {
        const start = set(filter.startDate, {
            hours: 0,
            minutes: 0,
            seconds: 0,
        }).toISOString();
        const end = set(filter.endDate, {
            hours: 23,
            minutes: 59,
            seconds: 59,
        }).toISOString();
        return { start, end };
    };

    const handleExportPaiement = async () => {
        const { start, end } = getDateRange();
        try {
            setLoading(true);
            const blob = await downloadPaiementExport(start, end, filter.paymentMethod);
            if (!blob) return;
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = `export-paiement-${new Date().toLocaleDateString()}.csv`;
            link.click();
            onClose();
        } catch (error) {
            console.error('Erreur export paiements:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleExportComptable = async () => {
        const { start, end } = getDateRange();
        try {
            setLoading(true);
            const blob = await downloadComptableExport(start, end);
            if (!blob) return;
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = `export-comptable-${new Date().toLocaleDateString()}.csv`;
            link.click();
            onClose();
        } catch (error) {
            console.error('Erreur export comptable:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleExportSepaXml = async () => {
        const { start, end } = getDateRange();
        try {
            setLoading(true);
            const blob = await downloadSepaXml(
                start,
                end,
                sepaOptions.requestedCollectionDate,
                sepaOptions.sequenceType,
            );
            if (!blob) return;
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = `prelevement-sepa-${new Date().toLocaleDateString()}.xml`;
            link.click();
            onClose();
        } catch (error) {
            console.error('Erreur export Prélèvement SEPA:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <ModalOverlay className="bg-[#000] !opacity-30" />
            <ModalContent className="!z-[1002] !m-auto !w-max min-w-[350px] !max-w-[85%] md:top-[12vh]">
                <ModalBody>
                    <Card extra="px-[30px] pt-[35px] pb-[40px] max-w-[650px] flex flex-col !z-[1004]">
                        <h1 className="text-xl font-bold">Export des paiements</h1>
                        <p className="mt-3 text-sm">
                            💡 Cet export vous permet de récupérer vos paiements ou écritures comptables au format CSV, ou de générer un fichier de Prélèvement SEPA (XML).
                        </p>

                        <div className="flex gap-4 mt-5">
                            <DateRangePicker
                                label="Dates de paiement*"
                                defaultStartDate={filter.startDate}
                                defaultEndDate={filter.endDate}
                                onChange={handleDateChange}
                            />
                            <div className="w-[200px]">
                                <SearchSelector
                                    label="Moyen de paiement*"
                                    displayKey="name"
                                    inputSmall={true}
                                    value={filter.paymentMethod}
                                    options={[
                                        { _id: 'all', name: 'Tous' },
                                        { _id: 'cash', name: 'Espèces' },
                                        { _id: 'check', name: 'Chèque' },
                                        { _id: 'transfer', name: 'Virement' },
                                        { _id: 'credit-card', name: 'Carte bancaire' },
                                        { _id: 'direct-debit', name: 'Prélèvement' },
                                    ]}
                                    onSelect={(e: any) => {
                                        setFilter((prev: any) => ({
                                            ...prev,
                                            paymentMethod: e._id,
                                        }));
                                    }}
                                />
                            </div>
                        </div>

                        <div className="mt-5">
                            <p className="text-xs font-medium text-gray-600">
                                Options du Prélèvement SEPA
                            </p>
                            <div className="flex gap-4 mt-2">
                                <div>
                                    <label className="text-xs font-medium text-gray-600">
                                        Date d'échéance*
                                    </label>
                                    <input
                                        type="date"
                                        value={sepaOptions.requestedCollectionDate}
                                        min={minCollectionDate}
                                        onChange={(e: any) => {
                                            setSepaOptions((prev: any) => ({
                                                ...prev,
                                                requestedCollectionDate: e.target.value,
                                            }));
                                        }}
                                        className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs text-navy-700 focus:outline-none focus:ring-2 focus:ring-toola-500"
                                    />
                                </div>
                                <div className="w-[220px]">
                                    <SearchSelector
                                        label="Type de séquence"
                                        displayKey="name"
                                        inputSmall={true}
                                        value={sepaOptions.sequenceType}
                                        options={[
                                            { _id: 'RCUR', name: 'Récurrent (RCUR)' },
                                            { _id: 'FRST', name: '1er prélèvement (FRST)' },
                                            { _id: 'FNAL', name: 'Dernier de la série (FNAL)' },
                                            { _id: 'OOFF', name: 'Ponctuel (OOFF)' },
                                        ]}
                                        onSelect={(e: any) => {
                                            setSepaOptions((prev: any) => ({
                                                ...prev,
                                                sequenceType: e._id,
                                            }));
                                        }}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="mt-8 flex flex-wrap gap-2">
                            <button
                                onClick={onClose}
                                className="linear rounded-xl border-2 border-gray-700 px-5 py-3 text-base font-medium text-gray-700 transition duration-200 hover:bg-red-600/5 active:bg-red-700/5 dark:border-red-400 dark:bg-red-400/10 dark:text-white"
                            >
                                Annuler
                            </button>

                            <button
                                disabled={loading}
                                onClick={handleExportPaiement}
                                className="linear col-span-12 ml-3 flex items-center justify-center rounded-xl bg-toola-500 px-3 py-3 text-xs font-medium text-white hover:bg-toola-600 active:bg-toola-700 sm:col-span-6 lg:col-span-4"
                            >
                                {loading ? 'Export...' : 'Export Paiements'}
                            </button>

                            <button
                                disabled={loading}
                                onClick={handleExportComptable}
                                className="linear col-span-12 ml-3 flex items-center justify-center rounded-xl bg-green-600 px-3 py-3 text-xs font-medium text-white hover:bg-green-700 active:bg-green-800 sm:col-span-6 lg:col-span-4"
                            >
                                {loading ? 'Export...' : 'Export Comptable'}
                            </button>

                            <button
                                disabled={loading}
                                onClick={handleExportSepaXml}
                                className="linear col-span-12 ml-3 flex items-center justify-center rounded-xl bg-purple-600 px-3 py-3 text-xs font-medium text-white hover:bg-purple-700 active:bg-purple-800 sm:col-span-6 lg:col-span-4"
                            >
                                {loading ? 'Export...' : 'Export Prélèvement SEPA'}
                            </button>
                        </div>
                    </Card>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default PaiementExportModal;

